import { create } from "zustand";
import { useNotificationStore } from "@/stores/notifications";
import { API_BASE } from "@/api/config";

export interface User {
  id: string;
  email: string;
  name: string | null;
  subscription_status: string;
  effective_tier: string;
  is_superadmin: boolean;
  is_admin: boolean;
  is_root_admin: boolean;
  has_artist_profile: boolean;
  created_at: string | null;
  welcome_shown_at: string | null;
  welcome_celebration_seen_at: string | null;
  is_artist_only_producer: boolean;
  ai_enabled: boolean;
  ai_monthly_limit: number | null;
}

export interface OrgBrief {
  id: string;
  name: string;
  slug: string;
  is_personal: boolean;
  artist_self_service_enabled: boolean;
  safe_edit_enabled: boolean;
}

interface AuthState {
  user: User | null;
  accessToken: string | null;
  refreshToken: string | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  organizations: OrgBrief[];
  activeOrganizationId: string | null;
  login: (email: string, password: string) => Promise<void>;
  register: (
    email: string,
    password: string,
    name?: string,
    captchaToken?: string,
  ) => Promise<void>;
  verifyEmail: (token: string) => Promise<void>;
  logout: () => void;
  refresh: () => Promise<boolean>;
  initialize: () => Promise<void>;
  fetchMe: () => Promise<void>;
  setActiveOrganization: (orgId: string | null) => void;
  loadOrganizations: () => Promise<void>;
}

// Shared in-flight refresh promise. If 10 concurrent requests hit 401 at once,
// they all await the same underlying /auth/refresh call instead of racing.
// A different account must start its own operation and ignore late responses.
let refreshInFlight: {
  refreshToken: string;
  promise: Promise<boolean>;
} | null = null;

export const useAuthStore = create<AuthState>((set, get) => ({
  user: null,
  accessToken: localStorage.getItem("access_token"),
  refreshToken: localStorage.getItem("refresh_token"),
  isAuthenticated: !!localStorage.getItem("access_token"),
  isLoading: true,
  organizations: [],
  activeOrganizationId: localStorage.getItem("active_org_id"),

  login: async (email: string, password: string) => {
    const resp = await fetch(`${API_BASE}/auth/login`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ email, password }),
    });
    if (!resp.ok) {
      const err = await resp.json().catch(() => ({ detail: "Login failed" }));
      throw new Error(err.detail || "Login failed");
    }
    const data = await resp.json();
    localStorage.setItem("access_token", data.access_token);
    localStorage.setItem("refresh_token", data.refresh_token);

    // Fetch user profile
    const meResp = await fetch(`${API_BASE}/auth/me`, {
      headers: { Authorization: `Bearer ${data.access_token}` },
    });
    const user = meResp.ok ? await meResp.json() : null;

    set({
      accessToken: data.access_token,
      refreshToken: data.refresh_token,
      isAuthenticated: true,
      user,
      isLoading: false,
    });

    // Load organizations after login
    await get().loadOrganizations();
  },

  register: async (
    email: string,
    password: string,
    name?: string,
    captchaToken?: string,
  ) => {
    const resp = await fetch(`${API_BASE}/auth/register`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        email,
        password,
        name,
        captcha_token: captchaToken || "",
      }),
    });
    if (!resp.ok) {
      const err = await resp
        .json()
        .catch(() => ({ detail: "Registration failed" }));
      throw new Error(err.detail || "Registration failed");
    }
    // No tokens returned — user must verify email first
  },

  verifyEmail: async (token: string) => {
    const resp = await fetch(`${API_BASE}/auth/verify-email`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ token }),
    });
    if (!resp.ok) {
      const err = await resp
        .json()
        .catch(() => ({ detail: "Verification failed" }));
      throw new Error(err.detail || "Verification failed");
    }
    const data = await resp.json();
    localStorage.setItem("access_token", data.access_token);
    localStorage.setItem("refresh_token", data.refresh_token);

    // Fetch user profile
    const meResp = await fetch(`${API_BASE}/auth/me`, {
      headers: { Authorization: `Bearer ${data.access_token}` },
    });
    const user = meResp.ok ? await meResp.json() : null;

    set({
      accessToken: data.access_token,
      refreshToken: data.refresh_token,
      isAuthenticated: true,
      user,
      isLoading: false,
    });

    // Load organizations (may include auto-accepted invites)
    await get().loadOrganizations();
  },

  logout: () => {
    localStorage.removeItem("access_token");
    localStorage.removeItem("refresh_token");
    localStorage.removeItem("active_org_id");
    set({
      user: null,
      accessToken: null,
      refreshToken: null,
      isAuthenticated: false,
      isLoading: false,
      organizations: [],
      activeOrganizationId: null,
    });
  },

  fetchMe: async () => {
    const token = get().accessToken;
    if (!token) return;
    try {
      const resp = await fetch(`${API_BASE}/auth/me`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (resp.ok) {
        const user = await resp.json();
        if (get().accessToken !== token) return;
        set({ user });
      }
    } catch {
      // silently fail — caller can decide whether to retry
    }
  },

  setActiveOrganization: (orgId: string | null) => {
    if (orgId) {
      localStorage.setItem("active_org_id", orgId);
    } else {
      localStorage.removeItem("active_org_id");
    }
    set({ activeOrganizationId: orgId });
  },

  loadOrganizations: async () => {
    const token = get().accessToken;
    if (!token) return;
    try {
      const resp = await fetch(`${API_BASE}/organizations`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (resp.ok) {
        const orgs = await resp.json();
        if (get().accessToken !== token) return;
        set({ organizations: orgs });
        // Auto-select personal org if none active
        const activeId = get().activeOrganizationId;
        if (!activeId && orgs.length > 0) {
          const personal = orgs.find((o: OrgBrief) => o.is_personal);
          const orgId = personal?.id || orgs[0].id;
          localStorage.setItem("active_org_id", orgId);
          set({ activeOrganizationId: orgId });
        }
      }
    } catch {
      // silently fail — orgs are non-critical
    }
  },

  refresh: async () => {
    const rt = get().refreshToken;
    if (!rt) {
      get().logout();
      return false;
    }
    if (refreshInFlight?.refreshToken === rt) return refreshInFlight.promise;

    const operation = { refreshToken: rt, promise: Promise.resolve(false) };
    refreshInFlight = operation;
    operation.promise = (async () => {
      try {
        const resp = await fetch(`${API_BASE}/auth/refresh`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ refresh_token: rt }),
          signal: AbortSignal.timeout(30_000),
        });
        if (!resp.ok) {
          if (get().refreshToken === rt) get().logout();
          return false;
        }
        const data = await resp.json();
        if (get().refreshToken !== rt) return false;
        localStorage.setItem("access_token", data.access_token);
        localStorage.setItem("refresh_token", data.refresh_token);
        set({
          accessToken: data.access_token,
          refreshToken: data.refresh_token,
          isAuthenticated: true,
        });
        return true;
      } catch {
        if (get().refreshToken === rt) get().logout();
        return false;
      } finally {
        if (refreshInFlight === operation) refreshInFlight = null;
      }
    })();

    return operation.promise;
  },

  initialize: async () => {
    const token = get().accessToken;
    let requestToken = token;
    if (!token) {
      set({ isLoading: false, isAuthenticated: false });
      return;
    }
    try {
      const resp = await fetch(`${API_BASE}/auth/me`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (get().accessToken !== token) return;
      if (resp.ok) {
        const user = await resp.json();
        if (get().accessToken !== token) return;
        set({ user, isAuthenticated: true, isLoading: false });
        await get().loadOrganizations();
        useNotificationStore.getState().fetchUnreadCount();
      } else if (resp.status === 401) {
        const refreshingUserId = get().user?.id ?? null;
        const refreshed = await get().refresh();
        if (refreshed) {
          if ((get().user?.id ?? null) !== refreshingUserId) return;
          const newToken = get().accessToken;
          if (newToken) {
            requestToken = newToken;
            const retry = await fetch(`${API_BASE}/auth/me`, {
              headers: { Authorization: `Bearer ${newToken}` },
            });
            if (get().accessToken !== newToken) return;
            if (retry.ok) {
              const user = await retry.json();
              if (get().accessToken !== newToken) return;
              set({ user, isAuthenticated: true, isLoading: false });
              await get().loadOrganizations();
              useNotificationStore.getState().fetchUnreadCount();
              return;
            }
          }
        }
        // refresh failed → logout() already called inside refresh()
        if (get().accessToken === requestToken) set({ isLoading: false });
      } else {
        if (get().accessToken === requestToken) set({ isLoading: false });
      }
    } catch {
      if (get().accessToken === requestToken) set({ isLoading: false });
    }
  },
}));
